import React, { useState, useEffect } from 'react';
import { passwordResetAPI, userAPI } from '../../services/api';
import { FiKey, FiCopy, FiTrash2, FiClock, FiCheck, FiX } from 'react-icons/fi';
import '../Auth.css';

const ManagePasswordResets = () => {
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [selectedUser, setSelectedUser] = useState('');
  const [resetTokens, setResetTokens] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [generatedLink, setGeneratedLink] = useState(null);

  useEffect(() => {
    fetchUsers();
    fetchResetTokens();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const fetchUsers = async () => {
    try {
      setLoadingUsers(true);
      const response = await userAPI.getAllUsers();
      console.log('Users response:', response.data);
      setUsers(response.data.users || []);
      setError('');
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchResetTokens = async () => {
    try {
      setLoading(true);
      const response = await passwordResetAPI.getAllResetTokens({ status: statusFilter });
      setResetTokens(response.data.tokens || []);
    } catch (err) {
      setError('Failed to fetch reset tokens');
      console.error('Error fetching reset tokens:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateLink = async (e) => {
    e.preventDefault();

    if (!selectedUser) {
      setError('Please select a user');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const response = await passwordResetAPI.generateResetLink({
        user_id: parseInt(selectedUser)
      });

      setGeneratedLink(response.data);
      setSuccess(`Password reset link generated for ${response.data.user.full_name}`);
      setSelectedUser('');
      fetchResetTokens();

      // Clear success message after 5 seconds
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to generate reset link');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = (link) => {
    navigator.clipboard.writeText(link);
    setSuccess('Link copied to clipboard!');
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleRevokeToken = async (tokenId) => {
    if (!window.confirm('Are you sure you want to revoke this reset token?')) {
      return;
    }

    try {
      await passwordResetAPI.revokeResetToken(tokenId);
      setSuccess('Reset token revoked successfully');
      fetchResetTokens();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to revoke token');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (token) => {
    if (token.is_used) {
      return <span className="badge badge-closed"><FiCheck /> Used</span>;
    }

    const isExpired = new Date(token.expires_at) < new Date();
    if (isExpired) {
      return <span className="badge badge-withdrawn"><FiX /> Expired</span>;
    }

    return <span className="badge badge-open"><FiClock /> Active</span>;
  };

  return (
    <div className="auth-container">
      <div className="auth-card" style={{ maxWidth: '1200px' }}>
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <FiKey size={48} style={{ color: 'var(--primary)' }} />
          <h1 style={{ margin: '16px 0 8px' }}>Manage Password Resets</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
            Generate password reset links for users
          </p>
        </div>

        {error && (
          <div className="error-message" style={{ marginBottom: '20px' }}>
            {error}
          </div>
        )}

        {success && (
          <div className="success-message" style={{ marginBottom: '20px' }}>
            {success}
          </div>
        )}

        {/* Generated Link Display */}
        {generatedLink && (
          <div style={{
            background: 'rgba(16, 185, 129, 0.1)',
            border: '2px solid #10b981',
            borderRadius: '8px',
            padding: '20px',
            marginBottom: '24px'
          }}>
            <h3 style={{ marginBottom: '12px', color: '#10b981' }}>
              <FiCheck /> Reset Link Generated
            </h3>
            <p style={{ marginBottom: '8px', fontSize: '14px' }}>
              <strong>User:</strong> {generatedLink.user.full_name} ({generatedLink.user.username})
            </p>
            <p style={{ marginBottom: '12px', fontSize: '14px' }}>
              <strong>Expires:</strong> {formatDate(generatedLink.expires_at)}
            </p>
            <div style={{
              display: 'flex',
              gap: '8px',
              alignItems: 'center',
              background: 'var(--bg-primary)',
              padding: '12px',
              borderRadius: '6px'
            }}>
              <input
                type="text"
                value={generatedLink.reset_link}
                readOnly
                style={{
                  flex: 1,
                  padding: '8px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '4px',
                  fontSize: '13px'
                }}
              />
              <button
                onClick={() => handleCopyLink(generatedLink.reset_link)}
                className="btn btn-primary"
                style={{ padding: '8px 16px' }}
              >
                <FiCopy /> Copy
              </button>
            </div>
          </div>
        )}

        {/* Generate New Reset Link Form */}
        <form onSubmit={handleGenerateLink} style={{ marginBottom: '32px' }}>
          <div className="form-group">
            <label className="form-label">Select User</label>
            {loadingUsers ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <div className="spinner" style={{ width: '30px', height: '30px' }}></div>
                <p style={{ marginTop: '8px', fontSize: '14px', color: 'var(--text-secondary)' }}>
                  Loading users...
                </p>
              </div>
            ) : users.length === 0 ? (
              <div style={{
                padding: '20px',
                textAlign: 'center',
                background: 'var(--bg-secondary)',
                borderRadius: '6px',
                color: 'var(--text-secondary)'
              }}>
                No users found. Please check your connection or permissions.
              </div>
            ) : (
              <select
                className="form-input"
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                required
              >
                <option value="">Choose a user...</option>
                {users.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.full_name} - {user.username} ({user.role})
                  </option>
                ))}
              </select>
            )}
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading || loadingUsers || !selectedUser}
            style={{ width: '100%' }}
          >
            <FiKey /> {loading ? 'Generating...' : 'Generate Reset Link'}
          </button>
        </form>

        <hr style={{ margin: '32px 0', border: 'none', borderTop: '1px solid var(--border-color)' }} />

        {/* Reset Tokens List */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h2 style={{ fontSize: '20px', margin: 0 }}>Reset Tokens</h2>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{
                padding: '8px 12px',
                border: '1px solid var(--border-color)',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            >
              <option value="all">All Tokens</option>
              <option value="active">Active</option>
              <option value="used">Used</option>
              <option value="expired">Expired</option>
            </select>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <div className="spinner"></div>
            </div>
          ) : resetTokens.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              color: 'var(--text-secondary)',
              background: 'var(--bg-secondary)',
              borderRadius: '8px'
            }}>
              No reset tokens found
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {resetTokens.map((token) => (
                <div
                  key={token.id}
                  style={{
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '8px',
                    padding: '16px'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                    <div>
                      <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                        {token.user_full_name}
                      </div>
                      <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                        {token.user_username} • {token.user_email}
                      </div>
                    </div>
                    {getStatusBadge(token)}
                  </div>

                  <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '8px' }}>
                    <div><strong>Created:</strong> {formatDate(token.created_at)}</div>
                    <div><strong>Expires:</strong> {formatDate(token.expires_at)}</div>
                    {token.is_used && token.used_at && (
                      <div><strong>Used:</strong> {formatDate(token.used_at)}</div>
                    )}
                    {token.created_by_username && (
                      <div><strong>Created by:</strong> {token.created_by_username}</div>
                    )}
                  </div>

                  {!token.is_used && new Date(token.expires_at) > new Date() && (
                    <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                      <button
                        onClick={() => handleCopyLink(`${process.env.REACT_APP_SERVER_URL || window.location.origin}/reset-password?token=${token.token}`)}
                        className="btn btn-secondary"
                        style={{ flex: 1, padding: '6px 12px', fontSize: '13px' }}
                      >
                        <FiCopy /> Copy Link
                      </button>
                      <button
                        onClick={() => handleRevokeToken(token.id)}
                        className="btn btn-secondary"
                        style={{ padding: '6px 12px', fontSize: '13px', color: '#ef4444' }}
                      >
                        <FiTrash2 /> Revoke
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManagePasswordResets;
